/**
 * OrganizationContext
 * Responsabilidade: estado global de organizações e tenant atual.
 */

import { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import type { PlanId } from '../domain/entities/Subscription';

export interface Organization {
    id: string;
    name: string;
    slug: string;
    plan: PlanId;
    status: 'active' | 'suspended' | 'cancelled' | 'trial';
    role: 'owner' | 'admin' | 'member' | 'viewer';
    trialEndsAt?: Date;
    subscriptionStatus?: 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete' | 'blocked';
    blockedAt?: Date;
}

export const CURRENT_ORG_STORAGE_KEY = 'currentOrganizationId';

interface OrganizationContextType {
    organizations: Organization[];
    currentOrganization: Organization | null;
    setCurrentOrganization: (org: Organization | null) => void;
    refreshOrganizations: (forceLoading?: boolean) => Promise<Organization[]>;
    isLoading: boolean;
    error: string | null;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

type OrgRow = {
    id: string;
    name: string;
    slug: string;
    plan: Organization['plan'];
    status: Organization['status'];
    trial_ends_at: string | null;
    subscription_status: Organization['subscriptionStatus'] | null;
    blocked_at: string | null;
};

function mapToOrganization(
    org: OrgRow,
    role: Organization['role']
): Organization {
    return {
        id: org.id,
        name: org.name,
        slug: org.slug,
        plan: org.plan,
        status: org.status,
        role,
        trialEndsAt: org.trial_ends_at ? new Date(org.trial_ends_at) : undefined,
        subscriptionStatus: org.subscription_status ?? undefined,
        blockedAt: org.blocked_at ? new Date(org.blocked_at) : undefined,
    };
}

export const OrganizationProvider = ({ children }: { children: ReactNode }) => {
    const { user, isAuthenticated } = useAuth();
    const userId = user?.id ?? null;
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [currentOrganization, setCurrentOrgState] = useState<Organization | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const currentOrgRef = useRef<Organization | null>(null);
    currentOrgRef.current = currentOrganization;

    const loadOrganizations = useCallback(async (forceLoading = false): Promise<Organization[]> => {
        if (!isAuthenticated || !userId) {
            setOrganizations([]);
            setCurrentOrgState(null);
            setIsLoading(false);
            return [];
        }

        try {
            // Boot only: keep existing UI mounted during background revalidation.
            if (forceLoading && !currentOrgRef.current) {
                setIsLoading(true);
            }
            setError(null);

            const { data: memberships, error: memberError } = await supabase
                .from('organization_members')
                .select('organization_id, role')
                .eq('user_id', userId);

            if (memberError) {
                console.error('[OrganizationContext] Error loading memberships:', memberError);
                setError(memberError.message);
                return [];
            }

            if (!memberships?.length) {
                setOrganizations([]);
                setCurrentOrgState(null);
                localStorage.removeItem(CURRENT_ORG_STORAGE_KEY);
                return [];
            }

            const orgIds = memberships.map((m) => m.organization_id);
            const { data: orgsData, error: orgsError } = await supabase
                .from('organizations')
                .select('id, name, slug, plan, status, trial_ends_at, subscription_status, blocked_at')
                .in('id', orgIds);

            if (orgsError) {
                console.error('[OrganizationContext] Error loading organizations:', orgsError);
                setError(orgsError.message);
                return [];
            }

            const orgById = new Map((orgsData ?? []).map((o) => [o.id, o as OrgRow]));
            const orgs = memberships.flatMap((membership) => {
                const org = orgById.get(membership.organization_id);
                if (!org) return [];
                return [mapToOrganization(org, membership.role as Organization['role'])];
            });

            setOrganizations(orgs);

            const savedOrgId = localStorage.getItem(CURRENT_ORG_STORAGE_KEY);
            const activeOrg = orgs.find((o) => o.id === savedOrgId) || orgs[0] || null;
            setCurrentOrgState(activeOrg);

            if (activeOrg) {
                localStorage.setItem(CURRENT_ORG_STORAGE_KEY, activeOrg.id);
            } else {
                localStorage.removeItem(CURRENT_ORG_STORAGE_KEY);
            }

            return orgs;
        } catch (err: unknown) {
            console.error('[OrganizationContext] Error:', err);
            const message = err instanceof Error ? err.message : 'Erro ao carregar organizações';
            setError(message);
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated, userId]);

    useEffect(() => {
        void loadOrganizations(true);
    }, [loadOrganizations]);

    const setCurrentOrganization = useCallback((org: Organization | null) => {
        setCurrentOrgState(org);
        if (org) {
            localStorage.setItem(CURRENT_ORG_STORAGE_KEY, org.id);
        } else {
            localStorage.removeItem(CURRENT_ORG_STORAGE_KEY);
        }
    }, []);

    const value = useMemo(
        () => ({
            organizations,
            currentOrganization,
            setCurrentOrganization,
            refreshOrganizations: loadOrganizations,
            isLoading,
            error,
        }),
        [organizations, currentOrganization, setCurrentOrganization, loadOrganizations, isLoading, error]
    );

    return (
        <OrganizationContext.Provider value={value}>
            {children}
        </OrganizationContext.Provider>
    );
};

export const useOrganization = (): OrganizationContextType => {
    const context = useContext(OrganizationContext);
    if (!context) {
        throw new Error('useOrganization must be used within OrganizationProvider');
    }
    return context;
};
